import {
  BillingBusinessLicense,
  BillingLicensePlans,
} from '@kizen/api/billing';
import { Button } from '@kizen/kds/Button';
import { Icon } from '@kizen/kds/Icon';
import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { useTranslation } from 'react-i18next';
import { SmallInput, TranslatedHeader } from './styles';
import { useEffect, useMemo, useState } from 'react';
import {
  formatPendingDate,
  getAnnualPrice,
  getDiscountedMonthlyPrice,
  getUnifiedPlan,
  interpolateValues,
} from '../util';
import { formatBillingCurrency } from '../../util';

type PlanCardProps = {
  plan?: BillingLicensePlans['license_templates'][number];
  currentPlan?: BillingBusinessLicense[number];
  period: string;
  unit?: string;
  isCurrentPlan?: boolean;
  isSelectedPlan?: boolean;
  isLegacy?: boolean;
  onSelectPlan?: () => void;
  numberInput?: boolean;
  numberInputLabel?: string;
  numberInputValue?: number;
  onNumberInputValueChange?: (value: number) => void;
  numberInputDisabled?: boolean;
  selectable?: boolean;
  isDowngradePlan?: boolean;
  onCancelDowngrade?: () => void;
  downgradeDate?: string;
  pendingDowngradeMaxUsers?: number;
  showAnnualPricing?: boolean;
  isCorePlan?: boolean;
};

const PlanPrice = ({
  amount,
  symbol,
  qualifier,
  prefix = '',
  boldAmount = true,
  compact = false,
}: {
  amount: number;
  symbol?: string;
  qualifier: string;
  prefix?: string;
  boldAmount?: boolean;
  compact?: boolean;
}) => {
  const formatted = formatBillingCurrency(amount, true, symbol ?? '$');
  const currencySymbol = symbol ?? '$';
  const amountText = formatted.startsWith(currencySymbol)
    ? formatted.slice(currencySymbol.length)
    : formatted;

  return (
    <span className="flex items-end">
      <Typography size={compact ? 'md' : 'lg'}>
        {prefix}
        {currencySymbol}
      </Typography>
      <Typography
        size={compact ? 'base' : 'xl'}
        weight={boldAmount ? 'bold' : 'regular'}
      >
        {amountText}
      </Typography>
      <Typography size="sm" color="font-secondary">
        {qualifier}
      </Typography>
    </span>
  );
};

const FEATURE_ITEM_LINE_HEIGHT_PX = 21;

const PlanFeatureItem = ({
  text,
  bulletColorClass,
}: {
  text: string;
  bulletColorClass: string;
}) => (
  <div className="flex gap-x-8 items-start">
    <span
      className="flex items-center shrink-0"
      style={{ height: FEATURE_ITEM_LINE_HEIGHT_PX }}
    >
      <Icon
        icon="custom-circle-check"
        size="base"
        className={`mt-[-10px] ${bulletColorClass}`}
      />
    </span>
    <Typography size="md">{text}</Typography>
  </div>
);

export const PlanCard = (props: PlanCardProps) => {
  const {
    plan: planProp,
    currentPlan: currentPlanProp,
    unit,
    period,
    isCurrentPlan,
    isSelectedPlan,
    isLegacy,
    onSelectPlan,
    numberInput,
    numberInputLabel,
    numberInputValue,
    onNumberInputValueChange,
    numberInputDisabled = false,
    selectable = true,
    isDowngradePlan,
    onCancelDowngrade,
    downgradeDate,
    pendingDowngradeMaxUsers,
    showAnnualPricing = false,
    isCorePlan = false,
  } = props;

  const { t } = useTranslation();

  const [inputTextValue, setInputTextValue] = useState(
    String(numberInputValue)
  );

  useEffect(() => {
    setInputTextValue(String(numberInputValue));
  }, [numberInputValue]);

  const plan = useMemo(() => {
    return getUnifiedPlan(planProp, currentPlanProp);
  }, [currentPlanProp, planProp]);

  const isFree = !plan?.price || plan.price.amount === 0;

  if (!plan) {
    return null;
  }

  const hasPendingDowngradeUserCount =
    typeof pendingDowngradeMaxUsers === 'number';

  const isPendingUserCountChange =
    hasPendingDowngradeUserCount &&
    pendingDowngradeMaxUsers !== Number(inputTextValue);

  const selectedPlanDowngradeText =
    isDowngradePlan && isPendingUserCountChange
      ? t('Cancel User Change')
      : t('Cancel Pending Plan');

  const showPendingDowngradeUserCountLabel =
    isPendingUserCountChange && downgradeDate;

  const showUserSelectionUI = !isFree && numberInput && isSelectedPlan;

  const bulletColorClass = isSelectedPlan
    ? 'text-border-active'
    : 'text-border-medium';

  const monthlyAmount = plan.price?.amount ?? 0;
  const pricePrefix = isCorePlan ? '' : '+';
  const perUnitQualifier = unit ? `/${unit}` : '';
  const monthlyQualifier = `${perUnitQualifier}/${period}`;
  const annualQualifier = `${perUnitQualifier}/yr`;

  const planAnnualDiscountPercentage = plan.annualDiscountPercentage ?? 0;
  const showAnnualLine = showAnnualPricing && !isFree;
  const displayedMonthlyAmount = showAnnualLine
    ? getDiscountedMonthlyPrice(monthlyAmount, planAnnualDiscountPercentage)
    : monthlyAmount;
  const annualAmount = getAnnualPrice(
    monthlyAmount,
    planAnnualDiscountPercentage
  );

  const planCardQaId = `plan-card-${plan.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')}`;

  return (
    <div
      key={plan.id}
      data-qa-id={planCardQaId}
      className="relative rounded-lg p-4 flex flex-col h-full"
      style={{
        border: isSelectedPlan
          ? '2px solid var(--color-border-active)'
          : '1px solid var(--color-border-medium)',
      }}
    >
      <div className="flex items-start">
        <Typography
          size="lg"
          weight={isSelectedPlan ? 'bold' : 'regular'}
          className="break-words min-w-0"
        >
          {plan.name}
        </Typography>
        <span className="ml-auto flex flex-col items-end gap-[9px]">
          <PlanPrice
            amount={displayedMonthlyAmount}
            symbol={plan.price?.symbol}
            qualifier={isFree ? `/${period}` : monthlyQualifier}
            prefix={pricePrefix}
          />
          {showAnnualLine ? (
            <PlanPrice
              amount={annualAmount}
              symbol={plan.price?.symbol}
              qualifier={annualQualifier}
              prefix={pricePrefix}
              boldAmount={false}
              compact
            />
          ) : null}
        </span>
      </div>
      <Spacer size={20} />
      <Typography weight="semibold">
        {plan?.ui_config?.highlights_section?.section_label ??
          t('Plan Highlights')}
      </Typography>
      <Spacer size={15} />
      {plan?.ui_config?.highlights_section?.items?.map((item: string) => {
        return (
          <div key={item} className="mb-2">
            <PlanFeatureItem
              text={interpolateValues(item, plan)}
              bulletColorClass={bulletColorClass}
            />
            <Spacer size={10} />
          </div>
        );
      })}
      <Spacer size={5} />
      <Typography weight="semibold">
        {plan?.ui_config?.limits_section?.section_label ?? t('Plan Limits')}
      </Typography>
      <Spacer size={15} />
      {plan?.ui_config?.limits_section?.items?.map((item: string) => {
        return (
          <div key={item}>
            <PlanFeatureItem
              text={interpolateValues(item, plan)}
              bulletColorClass={bulletColorClass}
            />
            <Spacer size={10} />
          </div>
        );
      })}
      <div
        className="flex justify-center mt-auto"
        data-qa-id="plan-card-action"
      >
        {isDowngradePlan ? (
          <Button
            variant="standard"
            outline
            color="warning"
            onClick={onCancelDowngrade}
            qa={{ id: 'cancel-pending-plan' }}
          >
            {selectedPlanDowngradeText}
          </Button>
        ) : (
          <Button
            variant="standard"
            outline={!isSelectedPlan}
            color="secondary"
            onClick={onSelectPlan}
            disabled={!selectable}
            qa={{ id: isSelectedPlan ? 'selected-plan' : 'select-plan' }}
          >
            {isSelectedPlan ? t('Selected Plan') : t('Select Plan')}
          </Button>
        )}
      </div>
      {isDowngradePlan ? (
        <div className="absolute top-0 right-0 left-0 flex items-center justify-center">
          <TranslatedHeader className="bg-feedback-toast-warning py-2 px-4 uppercase max-w-[90%]">
            <div>
              <Typography
                size="sm"
                weight="semibold"
                color="font-white"
                truncate
              >
                {downgradeDate
                  ? t('Pending {{date}}', {
                      date: formatPendingDate(downgradeDate),
                    })
                  : t('Pending')}
                {isLegacy ? ` (${t('Legacy')})` : ''}
              </Typography>
            </div>
          </TranslatedHeader>
        </div>
      ) : isCurrentPlan ? (
        <div className="absolute top-0 right-0 left-0 flex items-center justify-center">
          <TranslatedHeader className="bg-feedback-info py-2 px-4 uppercase max-w-[90%]">
            <div>
              <Typography
                size="sm"
                weight="semibold"
                color="font-white"
                truncate
              >
                {t('Current Plan')}
                {isLegacy ? ` (${t('Legacy')})` : ''}
              </Typography>
            </div>
          </TranslatedHeader>
        </div>
      ) : null}

      {showUserSelectionUI ? (
        <div>
          <Spacer size={20} />
          <div className="flex items-center">
            <div className="flex grow">
              <Typography size="md">{numberInputLabel}</Typography>
            </div>
            <div className="w-24">
              <SmallInput
                value={inputTextValue === null ? '0' : inputTextValue}
                onChange={setInputTextValue}
                placeholder="3"
                disabled={hasPendingDowngradeUserCount || numberInputDisabled}
                onBlur={() => {
                  if (!inputTextValue || isNaN(Number(inputTextValue))) {
                    setInputTextValue('0');
                    onNumberInputValueChange?.(0);
                  } else {
                    onNumberInputValueChange?.(Number(inputTextValue));
                  }
                }}
              />
            </div>
          </div>
          {showPendingDowngradeUserCountLabel ? (
            <>
              <Spacer size={15} />
              <Typography
                className="text-center "
                size="sm"
                fontStyle="italic"
                color="font-placeholder"
              >
                {t('Pending downgrade to {{maxUsers}} users on {{date}}', {
                  maxUsers: pendingDowngradeMaxUsers,
                  date: formatPendingDate(downgradeDate),
                })}
              </Typography>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
